import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../ui/dialog";
import { api, formatApiError } from "../../../lib/api";
import CountryPhoneInput from "../CountryPhoneInput";

const field = "w-full h-11 px-3 rounded-lg border border-slate-300 focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/15 outline-none text-sm";

/** Add or edit a saved parent. `parent` null → create. */
export default function ParentDialog({ open, onOpenChange, parent, initialName = "", onSaved }) {
    const [name, setName] = useState(parent?.name || initialName);
    const [student, setStudent] = useState(parent?.student_name || "");
    const [phone, setPhone] = useState(parent?.phone ? `+${parent.phone}` : "");
    const [valid, setValid] = useState(!!parent?.phone);
    const [saving, setSaving] = useState(false);

    const save = async (e) => {
        e.preventDefault();
        if (!name.trim()) return toast.error("Enter the parent's name");
        if (!valid) return toast.error("Enter a valid WhatsApp number for the selected country");
        setSaving(true);
        try {
            const body = { name: name.trim(), phone, student_name: student.trim() };
            const res = parent?.id
                ? await api.put(`/admin/fee-reminders/parents/${parent.id}`, body)
                : await api.post("/admin/fee-reminders/parents", body);
            if (res.data.existing) toast.info(`This number is already saved as ${res.data.parent.name} — selected that record`);
            else toast.success(parent?.id ? "Parent updated" : "Parent saved");
            onSaved(res.data.parent);
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md z-[70]" data-testid="fee-parent-dialog">
                <DialogHeader>
                    <DialogTitle>{parent?.id ? "Edit parent" : "Add new parent"}</DialogTitle>
                    <DialogDescription>Saved privately in the admin dashboard for future reminders.</DialogDescription>
                </DialogHeader>
                <form onSubmit={save} className="space-y-4">
                    <label className="block text-sm font-semibold text-slate-700">Parent name
                        <input className={`${field} mt-1`} value={name} onChange={(e) => setName(e.target.value)} data-testid="fee-parent-name" />
                    </label>
                    <div className="text-sm font-semibold text-slate-700">WhatsApp number
                        <div className="mt-1"><CountryPhoneInput value={phone} testid="fee-parent-phone"
                            onChange={(v, meta) => { setPhone(v); setValid(meta.valid); }} /></div>
                    </div>
                    <label className="block text-sm font-semibold text-slate-700">Student name <span className="font-normal text-slate-400">(optional)</span>
                        <input className={`${field} mt-1`} value={student} onChange={(e) => setStudent(e.target.value)} data-testid="fee-parent-student" />
                    </label>
                    <button type="submit" disabled={saving} data-testid="fee-parent-save"
                        className="w-full h-11 rounded-lg bg-[#1E3A8A] text-white font-bold hover:bg-[#172E6E] disabled:opacity-60 inline-flex items-center justify-center gap-2">
                        {saving && <Loader2 className="w-4 h-4 animate-spin" />} {parent?.id ? "Save changes" : "Save parent"}
                    </button>
                </form>
            </DialogContent>
        </Dialog>
    );
}
